import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/auth'
import { enqueueWrite } from './offline-queue'

// Configuración base
/**
 * Base de la API.
 *
 * Se exporta porque hay URLs que no pasan por axios: las descargas de Excel se
 * disparan con un enlace, y un `<a href>` no hereda el `baseURL` de la
 * instancia. Sin este prefijo, el fichero acabaria pidiendo `/dashboard/...`
 * en lugar de `/api/v1/dashboard/...` y devolveria un 404.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: false,
})

// Claves para idempotencia
const IDEMPOTENCY_HEADER = 'Idempotency-Key'

/**
 * Error personalizado para peticiones encoladas offline
 */
export class OfflineError extends Error {
  public readonly config: InternalAxiosRequestConfig
  public readonly queuedId: string

  constructor(message: string, config: InternalAxiosRequestConfig, queuedId = '') {
    super(message)
    this.name = 'OfflineError'
    this.config = config
    this.queuedId = queuedId
  }

  static isOfflineError(error: unknown): error is OfflineError {
    return error instanceof OfflineError
  }
}

// Interceptor de petición: adjuntar token de acceso y manejar cola offline
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore()

    if (authStore.accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${authStore.accessToken}`
    }

    // Generar Idempotency-Key para escrituras si no existe
    const isWrite = ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() ?? '')
    if (isWrite && config.headers && !config.headers[IDEMPOTENCY_HEADER]) {
      config.headers[IDEMPOTENCY_HEADER] = generateIdempotencyKey()
    }

    // Si estamos offline y es una escritura, encolar en lugar de fallar
    if (isWrite && !navigator.onLine) {
      const idempotencyKey = config.headers[IDEMPOTENCY_HEADER] as string
      
      // Convertir headers a objeto plano
      const headersObj: Record<string, string> = {}
      if (config.headers) {
        for (const [key, value] of Object.entries(config.headers)) {
          if (typeof value === 'string') {
            headersObj[key] = value
          }
        }
      }
      
      // Encolar la escritura para sincronización posterior
      await enqueueWrite(
        config.url ?? '',
        config.method?.toUpperCase() as 'POST' | 'PUT' | 'PATCH' | 'DELETE',
        config.data,
        headersObj,
        idempotencyKey
      )

      // Rechazar la petición original con un error controlado
      throw new OfflineError('Encolado para sincronización offline', config)
    }

    return config
  },
  (error: AxiosError) => Promise.reject(error),
)

// Interceptor de respuesta: manejar refresh, normalizar errores, y errores offline
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError | OfflineError) => {
    // Error offline controlado: no mostrar toast, solo propagar
    if (OfflineError.isOfflineError(error)) {
      return Promise.reject(error)
    }

    const originalRequest = (error as AxiosError).config as InternalAxiosRequestConfig & { _retry?: boolean }
    const authStore = useAuthStore()

    // 401: token expirado o inválido
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Si es el endpoint de refresh, no reintentar
      if (originalRequest.url?.includes('/auth/refresh')) {
        await authStore.logout()
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        await authStore.refreshAccessToken()

        // Reintentar la petición original con el nuevo token
        if (originalRequest.headers && authStore.accessToken) {
          originalRequest.headers.Authorization = `Bearer ${authStore.accessToken}`
        }

        return api(originalRequest)
      } catch (refreshError) {
        await authStore.logout()
        return Promise.reject(refreshError)
      }
    }

    // 403: permisos insuficientes
    if (error.response?.status === 403) {
      const message = normalizeErrorMessage(error)
      showErrorToast(message)
      return Promise.reject(createApiError(error, message, 'forbidden'))
    }

    // 422: validación
    if (error.response?.status === 422) {
      // Los errores de validación se manejan en el componente
      return Promise.reject(normalizeValidationError(error))
    }

    // 429: rate limit
    if (error.response?.status === 429) {
      const message =
        mensajeDeCuentaBloqueada(error) ??
        `Demasiadas peticiones. Intenta de nuevo en ${error.response.headers['retry-after'] ?? '60'} segundos.`

      showErrorToast(message)

      return Promise.reject(createApiError(error, message, 'rate_limit_exceeded'))
    }

    // 5xx: error del servidor
    if (error.response && error.response.status >= 500) {
      const message = 'Error interno del servidor. Intenta de nuevo más tarde.'
      showErrorToast(message)
      return Promise.reject(createApiError(error, message, 'internal_error'))
    }

    // Error de red / timeout (pero no error offline controlado)
    if (!error.response && !OfflineError.isOfflineError(error)) {
      const message = 'Error de conexión. Verifica tu conexión a internet.'
      showErrorToast(message)
      return Promise.reject(createApiError(error, message, 'network_error'))
    }

    // Otros errores: normalizar y propagar
    return Promise.reject(createApiError(error, normalizeErrorMessage(error), 'unknown_error'))
  },
)

/**
 * Normaliza el mensaje de error desde la respuesta del servidor
 */
/**
 * El mensaje de un 429 que el servidor ya ha escrito, cuando es un bloqueo de cuenta.
 *
 * Un 429 no siempre es lo mismo, y por eso hay dos caminos:
 *
 * - El **limitador de peticiones** avisa con la cabecera `Retry-After`, en segundos. Es
 *   un aviso de «has ido muy rápido», y el texto del cliente con el número de la
 *   cabecera es exactamente lo que hay que decir.
 * - Una **cuenta bloqueada por intentos fallidos** es otra cosa: no es que hayas ido
 *   rápido, es que la contraseña no ha sido la correcta cinco veces. El servidor lo
 *   dice con su propio mensaje y manda en `error.details.retry_after_minutes` los
 *   minutos que faltan.
 *
 * Antes se ignoraba el mensaje del servidor y se armaba siempre el propio con la
 * cabecera, así que una cuenta bloqueada quince minutos llegaba a pantalla como
 * «intenta de nuevo en 60 segundos». Quien lo leía esperaba un minuto, lo intentaba y se
 * volvía a encontrar con lo mismo, sin ninguna pista de por qué ni de cuánto faltaba.
 *
 * Solo se usa el mensaje del servidor cuando viene ese `retry_after_minutes`: es lo que
 * distingue un bloqueo de cuenta de un limitador, y así un 429 cualquiera sigue
 *Traduciéndose con el texto propio.
 */
function mensajeDeCuentaBloqueada(error: AxiosError): string | null {
  const envelope = error.response?.data as
    | { error?: { message?: string; details?: { retry_after_minutes?: unknown } } }
    | undefined

  const minutos = envelope?.error?.details?.retry_after_minutes

  if (typeof minutos !== 'number' || minutos <= 0) return null

  const base =
    envelope?.error?.message ?? 'Cuenta bloqueada temporalmente por intentos fallidos.'

  return `${base} Intenta de nuevo en ${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}.`
}

function normalizeErrorMessage(error: AxiosError): string {
  const response = error.response?.data as { error?: { message?: string; code?: string; details?: unknown }; message?: string } | undefined

  if (response?.error?.message) {
    return response.error.message
  }

  if (response?.message) {
    return response.message
  }

  const statusMessages: Record<number, string> = {
    400: 'Petición inválida.',
    401: 'No autenticado. Inicia sesión de nuevo.',
    403: 'No tienes permiso para realizar esta acción.',
    404: 'El recurso solicitado no existe.',
    405: 'Método no permitido.',
    409: 'Conflicto: el recurso ya existe.',
    422: 'Los datos enviados no son válidos.',
    429: 'Demasiadas peticiones. Intenta de nuevo más tarde.',
    500: 'Error interno del servidor.',
    502: 'Error del servidor. Intenta de nuevo más tarde.',
    503: 'Servicio no disponible temporalmente.',
    504: 'Tiempo de espera agotado.',
  }

  return statusMessages[error.response?.status ?? 0] ?? 'Ha ocurrido un error inesperado.'
}

/**
 * Normaliza errores de validación 422
 */
function normalizeValidationError(error: AxiosError): AxiosError & { validationErrors: Record<string, string[]> } {
  const response = error.response?.data as { error?: { details?: { fields?: Record<string, string[]> } } } | undefined

  const validationErrors = response?.error?.details?.fields ?? {}

  return Object.assign(error, { validationErrors })
}

/**
 * Crea un error de API estandarizado
 */
function createApiError(
  error: AxiosError,
  message: string,
  code: string,
): AxiosError & { apiError: { code: string; message: string; status: number } } {
  const apiError = {
    code,
    message,
    status: error.response?.status ?? 0,
  }

  return Object.assign(error, { apiError })
}

/**
 * Genera una clave de idempotencia única
 */
function generateIdempotencyKey(): string {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substring(2, 15)
  return `idem_${timestamp}_${random}`
}

/**
 * Muestra un toast de error usando PrimeVue ToastService
 */
function showErrorToast(message: string): void {
  try {
    // El toast se mostrará desde el componente que use el servicio
    // Aquí solo loggeamos para debugging
    console.warn('[API Error]', message)
  } catch {
    // Ignorar si ToastService no está disponible
  }
}

/**
 * Helper para peticiones con idempotencia explícita
 */
export async function idempotentRequest<T>(
  method: 'post' | 'put' | 'patch' | 'delete',
  url: string,
  data?: unknown,
  idempotencyKey?: string,
): Promise<T> {
  const headers: Record<string, string> = {}

  if (idempotencyKey) {
    headers[IDEMPOTENCY_HEADER] = idempotencyKey
  }

  const response = await api.request<T>({ method, url, data, headers })
  return response.data
}

/**
 * Tipos de respuesta de la API
 */
export interface ApiResponse<T> {
  data: T
  meta?: Record<string, unknown>
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  meta: {
    current_page: number
    last_page: number
    per_page: number
    total: number
    from: number | null
    to: number | null
    query: Record<string, unknown>
  }
}

export interface ApiError {
  code: string
  message: string
  details?: Record<string, unknown>
  meta?: Record<string, unknown>
}

export interface ApiErrorResponse {
  error: ApiError
}